import { useEffect, useMemo, useState } from "react";
import { FaClock, FaExpand, FaImage } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { buildRawImageUrl } from "./installationConfigUtils";

function formatDateTime(datetime) {
  const dateObj = new Date(datetime);
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

const InstallationVisualizationPanel = ({ routeCameraId, cycleEntries }) => {
  const { t } = useTranslation();
  const [zoomedImage, setZoomedImage] = useState("");
  const [zoomedSide, setZoomedSide] = useState("left");

  const recentLogEntry = useMemo(() => {
    const withImages = (cycleEntries || []).filter(
      (item) => item.log && (item.leftImage || item.rightImage),
    );
    if (withImages.length === 0) {
      return null;
    }
    return withImages.reduce((latest, current) => {
      const extractDateTime = (name) =>
        new Date(name.split("-camera-")[0].replace("_", ":").replace("_", ":"));
      return extractDateTime(latest.name) > extractDateTime(current.name)
        ? latest
        : current;
    });
  }, [cycleEntries]);

  const leftImageUrl = useMemo(() => {
    if (!recentLogEntry?.name) {
      return "";
    }
    return buildRawImageUrl(routeCameraId, recentLogEntry.name, "-L");
  }, [recentLogEntry, routeCameraId]);

  const rightImageUrl = leftImageUrl ? leftImageUrl.replace("-L", "-R") : "";

  useEffect(() => {
    if (leftImageUrl) {
      setZoomedSide("left");
      setZoomedImage(leftImageUrl);
    } else {
      setZoomedImage("");
    }
  }, [leftImageUrl]);

  const imageDatetime = recentLogEntry?.name
    ? recentLogEntry.name.split("-camera")[0].replace(/_/g, ":") + ".000Z"
    : "";

  const openZoom = (side) => {
    setZoomedSide(side);
    setZoomedImage(side === "left" ? leftImageUrl : rightImageUrl);
  };

  if (!leftImageUrl) {
    return (
      <div className="installation-viz">
        <div className="installation-viz-empty">
          <FaImage className="installation-viz-empty-icon" aria-hidden />
          <p className="installation-viz-empty-title">
            {t("installation_visualization_raw_title")}
          </p>
          <p className="installation-viz-empty-text">
            {t("installation_no_recent_image")}
          </p>
        </div>
      </div>
    );
  }

  const formattedTime = formatDateTime(imageDatetime);

  return (
    <div className="installation-viz">
      <div className="installation-viz-meta">
        <span className="installation-viz-badge">
          <FaClock aria-hidden />
          {t("installation_image_taken_at", { time: formattedTime })}
        </span>
      </div>

      <div className="installation-viz-thumbs">
        <button
          type="button"
          className={
            zoomedSide === "left"
              ? "installation-viz-thumb active"
              : "installation-viz-thumb"
          }
          onClick={() => openZoom("left")}
        >
          <span className="installation-viz-thumb-label">
            {t("installation_side_left")}
          </span>
          <img
            src={leftImageUrl}
            alt=""
            className="installation-viz-thumb-img"
          />
          <FaExpand className="installation-viz-thumb-expand" aria-hidden />
        </button>
        <button
          type="button"
          className={
            zoomedSide === "right"
              ? "installation-viz-thumb active"
              : "installation-viz-thumb"
          }
          onClick={() => openZoom("right")}
        >
          <span className="installation-viz-thumb-label">
            {t("installation_side_right")}
          </span>
          <img
            src={rightImageUrl}
            alt=""
            className="installation-viz-thumb-img"
          />
          <FaExpand className="installation-viz-thumb-expand" aria-hidden />
        </button>
      </div>

      <div className="installation-viz-stage">
        {zoomedImage ? (
          <img
            src={zoomedImage}
            alt=""
            className="installation-viz-stage-img"
          />
        ) : (
          <p className="installation-viz-stage-hint">
            {t("installation_tap_to_zoom")}
          </p>
        )}
      </div>
    </div>
  );
};

export default InstallationVisualizationPanel;
